import type { JSX } from 'react';
import { useRef } from 'react';
import type { Zone } from '@bratva/content';
import type { SaveDoc } from '@bratva/state';
import {
  DAY_LENGTH_MINUTES,
  HEAT_THRESHOLDS,
  stageOf,
  companionSlots,
  formatClock,
  isDayOver,
  minutesLeft,
} from '@bratva/rules';
import { store } from '../game/store.js';
import { carryCapacity, character } from '../game/character.js';

interface Props {
  save: SaveDoc;
  zone: Zone;
  onOpenCharacter(): void;
  onOpenQuests(): void;
  onOpenRuns(): void;
}

export function Hud({ save, zone, onOpenCharacter, onOpenQuests, onOpenRuns }: Props): JSX.Element {
  const fileInput = useRef<HTMLInputElement>(null);
  const player = save.player;
  const { vehicle } = character(save);
  const stage = stageOf(save.justice, save.day);
  const left = minutesLeft(save.minute);
  const dayOver = isDayOver(save.minute);

  const download = (): void => {
    const blob = new Blob([store.toFile()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bratva-delo-${save.day}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const upload = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    try {
      store.fromFile(await file.text());
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Файл не читается');
    }
  };

  return (
    <div className="hud">
      <div className="hud-top">
        <div className="clock">
          <b>{formatClock(save.minute)}</b>
          <span>
            день {save.day} · {zone.title} · ур. {player.level}
            {vehicle ? ` · ${vehicle.title}` : ''}
          </span>
        </div>
        <div className="daybar" title={`Осталось ${left} мин`}>
          <i style={{ width: `${(save.minute / DAY_LENGTH_MINUTES) * 100}%` }} />
        </div>
        <div className="cash">{(player.cash / 100).toLocaleString('ru-RU')} ₽</div>
      </div>

      <div className="hud-stats">
        <Meter label="Стресс" value={player.stress} tone={player.stress >= 60 ? 'warn' : 'ok'} />
        <Meter label="Палево" value={player.heat} tone={player.heat >= 60 ? 'crit' : 'ok'} />
        <Meter label="Здоровье" value={player.health} tone={player.health <= 30 ? 'crit' : 'ok'} />
      </div>

      <div className="hud-pockets">
        <p>
          Хабар{' '}
          <span>
            {save.habar.length} / {carryCapacity(save)}
          </span>
        </p>
        <p>
          Точки <span>{save.businesses.length}</span>
        </p>
        <p>
          Спутницы{' '}
          <span>
            {save.companions.length} / {companionSlots(player.obayanie)}
          </span>
        </p>
      </div>

      {zone.exits.length > 0 && (
        <div className="hud-exits">
          <span className="hud-caption">Отсюда можно</span>
          {zone.exits.map((exit) => (
            <p key={exit.to}>
              {exit.label} <span>{exit.minutes} мин дороги</span>
            </p>
          ))}
        </div>
      )}

      {stage !== 'clean' && (
        <p className="hud-alert">
          {stage === 'podpiska' && 'Подписка о невыезде: палево за ночь почти не спадает.'}
          {stage === 'sledstvie' &&
            `Следствие. Суд через ${Math.max(0, (save.justice.courtDay ?? save.day) - save.day)} дн. — вопрос надо решать со смотрящим.`}
          {stage === 'sud' && 'Сегодня суд. Если не решил — решат за тебя.'}
        </p>
      )}

      {player.heat >= HEAT_THRESHOLDS.hunt && !dayOver && (
        <p className="hud-alert">
          {player.heat >= HEAT_THRESHOLDS.raid
            ? 'Палево под потолком. Ночевать с таким — значит проснуться от стука в дверь.'
            : 'Тебя ищут: патрули смотрят вдвое дальше. Пора отсидеться или сбыть хабар.'}
        </p>
      )}

      {dayOver && (
        <p className="hud-alert">
          Одиннадцать вечера, дела кончились. Ночь спишет палево и стресс, а к утру двор будет другим.
        </p>
      )}

      <div className="hud-log">
        {save.log
          .slice(-6)
          .reverse()
          .map((entry, index) => (
            <p key={`${entry.day}-${entry.minute}-${index}`}>
              <span>{formatClock(entry.minute)}</span> {entry.text}
            </p>
          ))}
      </div>

      <div className="hud-actions">
        <button type="button" className={dayOver ? 'primary' : ''} onClick={() => store.dispatch({ type: 'sleep' })}>
          {dayOver ? 'Спать' : 'Спать раньше'}
        </button>
        <button
          type="button"
          className={player.statPoints > 0 ? 'primary' : ''}
          onClick={onOpenCharacter}
        >
          Пацан{player.statPoints > 0 ? ` (${player.statPoints})` : ''}
        </button>
        <button type="button" onClick={onOpenQuests}>
          Дела и люди
        </button>
        <button type="button" onClick={onOpenRuns}>
          Слоты
        </button>
        <button type="button" onClick={download}>
          Выгрузить дело
        </button>
        <button type="button" onClick={() => fileInput.current?.click()}>
          Загрузить дело
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json"
          hidden
          onChange={(event) => void upload(event.target.files?.[0])}
        />
      </div>

      <p className="hud-hint">
        Клик по земле — идти, клик по ларьку, тачке или дому — подойти и посмотреть, что там можно.
        Красные клетки на краю — дорога в соседнюю зону.
      </p>
    </div>
  );
}

function Meter({ label, value, tone }: { label: string; value: number; tone: 'ok' | 'warn' | 'crit' }): JSX.Element {
  return (
    <div className="meter">
      <span className="meter-label">{label}</span>
      <div className={`meter-bar ${tone}`}>
        <i style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
      </div>
      <span className="meter-value">{Math.round(value)}</span>
    </div>
  );
}
